import { inertDocument } from './inertDocument.ts';

/**
 * One person read out of a pasted list, before anything is created.
 *
 * `status` is where the row stands in the import: `pending` until it is its
 * turn, then `creating`, then the outcome. A `success` row carries the id of
 * the user it made.
 */
export interface ParsedUser {
  firstName: string;
  lastName: string;
  email: string;
  status: 'pending' | 'creating' | 'success' | 'error';
  errorMessage?: string;
  userId?: string;
}

/** The shape a mail address has to have to be worth trying. */
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Read people out of whatever was pasted.
 *
 * Deliberately forgiving: what arrives is a copy of somebody's spreadsheet, an
 * Outlook table or three lines typed by hand, and none of them agree on a
 * column order or a separator. A header row naming the columns decides their
 * order; without one the order is guessed. An HTML table wins where there is
 * one — its cells are unambiguous — and everything else is read line by line.
 * A row without a valid address is dropped: an import runs on addresses.
 */
export function parsePastedUsers(pasted: string): ParsedUser[] {
  const rows = inertDocument(pasted).querySelectorAll('tr');
  return rows.length > 0 ? fromTableRows(rows) : fromLines(pasted);
}

/* Last name, first name, email — the column order Outlook and Excel paste in. */
const TABLE_ORDER: Columns = { lastName: 0, firstName: 1, email: 2 };

function fromTableRows(rows: NodeListOf<HTMLTableRowElement>): ParsedUser[] {
  const cellsOf = (row: HTMLTableRowElement) =>
    Array.from(row.querySelectorAll('td, th'));
  const allRows = Array.from(rows);
  const columns = columnsFromHeader(
    cellsOf(allRows[0]!).map((cell) => cell.textContent ?? ''),
  );

  const users: ParsedUser[] = [];
  allRows.slice(1).forEach((row) => {
    if (!columns && row.querySelector('th')) return;

    const cells = cellsOf(row);
    const { firstName, lastName, email: emailIndex } = columns ?? TABLE_ORDER;

    /* The address is often a mailto link rather than plain text. */
    const emailCell = cells[emailIndex];
    const emailLink = emailCell?.querySelector('a');
    const email = (emailLink ?? emailCell)?.textContent?.trim() ?? '';

    if (email && isValidEmail(email)) {
      users.push({
        firstName: cells[firstName]?.textContent?.trim() ?? '',
        lastName: cells[lastName]?.textContent?.trim() ?? '',
        email,
        status: 'pending',
      });
    }
  });

  return users;
}

const SEPARATORS = ['\t', ',', ';', '|'];

function fromLines(pasted: string): ParsedUser[] {
  const lines = pasted
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const columns = lines.length > 0 ? columnsFromHeader(splitLine(lines[0]!)) : null;

  const users: ParsedUser[] = [];
  for (const line of columns ? lines.slice(1) : lines) {
    const parts = splitLine(line);
    const person = columns ? personFromColumns(parts, columns) : personFromParts(parts);

    if (person && isValidEmail(person.email)) {
      users.push({ ...person, status: 'pending' });
    }
  }

  return users;
}

function splitLine(line: string): string[] {
  for (const separator of SEPARATORS) {
    const parts = line.split(separator).map((part) => part.trim());
    if (parts.length >= 2) return parts;
  }

  /* "FirstName LastName email@example.com" — spaces are the last resort. */
  return line.split(/\s+/);
}

function personFromParts(
  parts: string[],
): { firstName: string; lastName: string; email: string } | null {
  if (parts.length >= 3) {
    /*
     * Two orders reach this line: "FirstName LastName Email" and
     * "LastName, FirstName, Email". Whether the last part is an address is
     * what tells them apart.
     */
    const lastPart = parts[parts.length - 1]!;
    if (isValidEmail(lastPart)) {
      return {
        firstName: parts[0]!,
        lastName: parts.slice(1, -1).join(' '),
        email: lastPart,
      };
    }
    return { lastName: parts[0]!, firstName: parts[1]!, email: parts[2]! };
  }

  if (parts.length === 2) {
    /* "FirstName email" or "LastName, FirstName" — the second has no address. */
    if (isValidEmail(parts[1]!)) {
      return { firstName: parts[0]!, lastName: '', email: parts[1]! };
    }
    return { lastName: parts[0]!, firstName: parts[1]!, email: '' };
  }

  return null;
}

/** Which cell holds which field. */
interface Columns {
  firstName: number;
  lastName: number;
  email: number;
}

/* A bare "Name" next to "First name" is the last name, as in Vlerick's lists. */
const HEADER_LABELS: Record<keyof Columns, string[]> = {
  firstName: ['firstname', 'givenname', 'voornaam'],
  lastName: ['lastname', 'surname', 'familyname', 'name', 'achternaam', 'naam'],
  email: ['email', 'emailaddress', 'mail', 'emailadres'],
};

/** The column order a header row names, or null when the row is not a header. */
function columnsFromHeader(cells: string[]): Columns | null {
  const labels = cells.map((cell) => cell.toLowerCase().replace(/[^a-z]/g, ''));
  const find = (field: keyof Columns) =>
    labels.findIndex((label) => HEADER_LABELS[field].includes(label));

  const columns = {
    firstName: find('firstName'),
    lastName: find('lastName'),
    email: find('email'),
  };
  return Object.values(columns).every((index) => index >= 0) ? columns : null;
}

function personFromColumns(
  parts: string[],
  columns: Columns,
): { firstName: string; lastName: string; email: string } {
  return {
    firstName: parts[columns.firstName] ?? '',
    lastName: parts[columns.lastName] ?? '',
    email: parts[columns.email] ?? '',
  };
}
